import { useContext, useEffect, useState } from 'react'
import MerchantPicker from '../../components/ui/MerchantPicker';
import Paginator from '../../components/ui/Paginator';
import { UsersContext } from '../../context/users/UserContext';
import { useNavigate } from 'react-router-dom';
import { AccessLevel, ManagedUser } from '../../models/interfaces';
import Message from '../../components/ui/Message';
import { LEVEL_ICON, LEVEL_LABEL, USER_TYPE_LABEL } from '../../utils/access';
import BankOptions from '../../components/ui/BankOptions';
import { SubscriptionHeader } from './SubscriptionHeader';

/**
 * Usuarios por nivel: plataforma, bancos y comercios. Cada administrador ve los de su alcance
 * (el banco, los de su banco y sus comercios; el comercio, los de su comercio).
 */
export const UsersPage = () => {
    const { users, totalPage, isLoading, catalog, loadUsers, loadCatalog, deleteUser, resetPassword } = useContext(UsersContext);
    const navigate = useNavigate();

    const [level, setLevel] = useState<AccessLevel | null>(null);
    const [bankId, setBankId] = useState<number | ''>('');
    const [merchantId, setMerchantId] = useState<number | ''>('');
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [pageActual, setPageActual] = useState(1);

    const [error, setError] = useState('');
    const [userToDelete, setUserToDelete] = useState<ManagedUser | null>(null);
    const [userToReset, setUserToReset] = useState<ManagedUser | null>(null);
    const [resettingId, setResettingId] = useState<number | null>(null);
    const [notice, setNotice] = useState('');

    // Catálogo: niveles, bancos y comercios de su alcance. Pestaña inicial: el nivel más alto que administra
    useEffect(() => {
        loadCatalog().then(result => {
            if (result?.levels?.length) setLevel(result.levels[0]);
        });
    }, [loadCatalog]);

    // Búsqueda en el servidor, 400 ms después de dejar de escribir
    useEffect(() => {
        const timer = setTimeout(() => { setAppliedSearch(search); setPageActual(1); }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (!level) return;
        loadUsers(level, pageActual - 1, { bankId: bankId || null, merchantId: merchantId || null, search: appliedSearch });
    }, [level, pageActual, bankId, merchantId, appliedSearch, loadUsers]);

    const changeLevel = (lvl: AccessLevel) => {
        setLevel(lvl);
        setBankId('');
        setMerchantId('');
        setPageActual(1);
    };

    const banks = catalog?.banks ?? [];
    const showBankFilter = level !== 'PLATAFORMA' && banks.length > 1;
    const showMerchantFilter = level === 'COMERCIO' && (catalog?.merchants?.length ?? 0) > 1;

    // Encabezado de la suscripción: el comercio de la sesión o el que filtró el banco/plataforma
    const merchants = catalog?.merchants ?? [];
    const subscriptionMerchantId = level === 'COMERCIO'
        ? (merchantId || (merchants.length === 1 ? merchants[0].id : null))
        : null;

    const ownerOf = (user: ManagedUser) =>
        user.merchant?.name ?? user.bank?.description ?? (level === 'PLATAFORMA' ? 'SwiftPay' : '-');

    const processDelete = async () => {
        const user = userToDelete;
        setUserToDelete(null);
        if (!user) return;
        const resp = await deleteUser(user.id);
        if (resp !== 'success') setError(resp);
    };

    const processReset = async () => {
        const user = userToReset;
        setUserToReset(null);
        if (!user) return;
        setResettingId(user.id);
        const resp = await resetPassword(user.id);
        setResettingId(null);
        if (resp.error) setError(resp.error);
        else setNotice(`Se envió una contraseña temporal a ${resp.email}. El usuario deberá cambiarla al iniciar sesión.`);
    };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Usuarios</h3>
                    <p className="text-muted mb-0">Administre los usuarios que tienen acceso al portal y al POS.</p>
                </div>
                <button type="button" className="btn btn-primary mt-2 mt-md-0" disabled={!level}
                    onClick={() => navigate('/user', { state: { level, bankId: bankId || null, merchantId: merchantId || null } })}>
                    <i className="bi bi-plus-lg me-1" /> Agregar usuario
                </button>
            </div>

            <SubscriptionHeader merchantId={subscriptionMerchantId} refreshKey={users} />

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    {(catalog?.levels?.length ?? 0) > 1 && (
                        <ul className="nav nav-tabs mb-3">
                            {catalog!.levels.map(lvl => (
                                <li className="nav-item" key={lvl}>
                                    <button className={`nav-link ${level === lvl ? 'active' : ''}`} onClick={() => changeLevel(lvl)}>
                                        <i className={`bi ${LEVEL_ICON[lvl]} me-1`} />{LEVEL_LABEL[lvl]}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="row g-2 mb-3">
                        <div className="col-md-5">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input className="form-control" id="search" type="text" placeholder="Buscar por usuario, nombre o correo..."
                                    value={search} onChange={(e) => setSearch(e.target.value)} />
                                {search && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setSearch('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {showBankFilter && (
                            <div className="col-md-3">
                                <select className="form-select" value={bankId} onChange={(e) => { setBankId(e.target.value ? Number(e.target.value) : ''); setMerchantId(''); setPageActual(1); }}>
                                    <option value="">Todos los bancos</option>
                                    <BankOptions banks={banks} />
                                </select>
                            </div>
                        )}
                        {showMerchantFilter && (
                            <div className="col-md-4">
                                <MerchantPicker id="usersMerchantFilter" value={merchantId || -1}
                                    params={bankId ? { bankId: String(bankId) } : undefined}
                                    onChange={(id) => { setMerchantId(id > 0 ? id : ''); setPageActual(1); }} />
                            </div>
                        )}
                    </div>

                    <div className='table-responsive'>
                        <table id='usersTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Usuario</th>
                                    <th>Nombre</th>
                                    <th>Correo</th>
                                    <th>Grupos</th>
                                    {level !== 'PLATAFORMA' && <th>{level === 'BANCO' ? 'Banco' : 'Comercio'}</th>}
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading || !level ? (
                                    <tr><td colSpan={6} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : users.length > 0 ? users.map(u => (
                                    <tr key={u.id}>
                                        <td style={styles.ellipsis}>
                                            <div className="fw-medium">{u.username}</div>
                                            {u.userType === 'OPERADOR' && <span className="badge bg-warning-subtle text-warning-emphasis">{USER_TYPE_LABEL.OPERADOR}</span>}
                                        </td>
                                        <td style={styles.ellipsis}>{`${u.name ?? ''} ${u.lastname ?? ''}`.trim() || '-'}</td>
                                        <td style={styles.ellipsis}>{u.email || '-'}</td>
                                        <td>
                                            {u.groups.map(group => (
                                                <span key={group.id} className="badge rounded-pill bg-primary-subtle text-primary-emphasis me-1">{group.name}</span>
                                            ))}
                                        </td>
                                        {level !== 'PLATAFORMA' && <td style={styles.ellipsis}>{ownerOf(u)}</td>}
                                        <td style={{ textAlign: 'center', width: 150 }}>
                                            {u.editable ? (
                                                <>
                                                    <button type="button" className="btn btn-sm btn-primary" title="Editar usuario"
                                                        onClick={() => navigate('/user', { state: { user: u, level } })}>
                                                        <i className="bi bi-pencil" />
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-outline-secondary ms-1" title="Restablecer contraseña"
                                                        aria-label={`Restablecer contraseña de ${u.username}`}
                                                        disabled={!u.email || resettingId === u.id}
                                                        onClick={() => setUserToReset(u)}>
                                                        {resettingId === u.id
                                                            ? <span className="spinner-border spinner-border-sm" />
                                                            : <i className="bi bi-key" />}
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-danger ms-1" title="Eliminar usuario" onClick={() => setUserToDelete(u)}>
                                                        <i className="bi bi-trash" />
                                                    </button>
                                                </>
                                            ) : (
                                                <i className="bi bi-lock text-muted" title="No puede modificar este usuario" />
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={6} className='text-center py-4 text-muted'>
                                        {appliedSearch ? 'No se encontraron usuarios para la búsqueda.' : 'No existen usuarios en este nivel.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-end align-items-center mt-2">
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {notice.length > 0 && (
                <Message type="success" title="Contraseña restablecida" message={notice} closeDialog={() => setNotice('')} handleOk={() => setNotice('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {userToReset && (
                <Message
                    type="info"
                    title="Restablecer contraseña"
                    message={`Se generará una contraseña temporal para "${userToReset.username}" y se enviará a ${userToReset.email}. La contraseña actual dejará de funcionar. ¿Desea continuar?`}
                    closeDialog={() => setUserToReset(null)}
                    showCancel={true}
                    okText="Restablecer"
                    handleCancel={() => setUserToReset(null)}
                    handleOk={processReset}
                    okWidth="120px"
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {userToDelete && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Está seguro que desea eliminar el usuario "${userToDelete.username}"?`}
                    closeDialog={() => setUserToDelete(null)}
                    showCancel={true}
                    handleCancel={() => setUserToDelete(null)}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    ellipsis: {
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: 260,
    },
};
